import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { useSelector } from 'react-redux'
import {
  MdExpandLess,
  MdExpandMore,
  MdSearch,
  MdWarningAmber
} from 'react-icons/md'
import Navbar from './Navbar'

export default function OverdueLoans() {
  const { url } = useSelector(state => state)

  const [loans, setLoans] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [expandedEmail, setExpandedEmail] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')

  const formatCurrency = amount =>
    `₦${Number(amount || 0).toLocaleString()}`

  const formatDate = value => {
    if (!value) return '—'

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return '—'

    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  useEffect(() => {
    const getOverdueLoans = async () => {
      try {
        setError('')

        const response = await axios.get(
          `${url}/admin/overdue-loans`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('access_token')}`
            }
          }
        )

        setLoans(response.data.overdue_loans || [])
      } catch (err) {
        console.error(err)
        setError(
          err.response?.data?.detail ||
          'Unable to load overdue loans.'
        )
      } finally {
        setLoading(false)
      }
    }

    getOverdueLoans()
  }, [url])

  const filteredLoans = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return loans

    return loans.filter(loan =>
      [loan.fullname, loan.email]
        .some(value => String(value || '').toLowerCase().includes(query))
    )
  }, [loans, searchQuery])

  const totalDefaultCharges = loans.reduce(
    (sum, loan) => sum + Number(loan.total_default_amount || 0),
    0
  )

  return (
    <div className="flex min-h-screen w-full bg-[#F7F9FC] font-[Satoshi]">
      <Navbar />

      <main className="min-w-0 flex-1 px-5 pb-10 pt-24 sm:px-6 lg:px-8 lg:pt-8">
        <div className="mx-auto max-w-[1500px]">
          <div className="mb-7 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-red-600">COLLECTIONS</p>
              <h1 className="mt-1 text-3xl font-bold tracking-tight text-gray-900">Overdue loans</h1>
              <p className="mt-2 text-sm text-gray-500">
                Customers who have passed their scheduled repayment date.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <div className="rounded-full border border-red-100 bg-red-50 px-3.5 py-2 text-xs font-semibold text-red-700">
                {loans.length} overdue account{loans.length === 1 ? '' : 's'}
              </div>
              <div className="rounded-full border border-gray-200 bg-white px-3.5 py-2 text-xs font-semibold text-gray-600">
                {formatCurrency(totalDefaultCharges)} default charges
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-6 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-600">
              {error}
            </div>
          )}

          {!loading && loans.length > 0 && (
            <div className="mb-5 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="relative max-w-xl">
                <MdSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-xl text-gray-400" />
                <input
                  value={searchQuery}
                  onChange={event => setSearchQuery(event.target.value)}
                  placeholder="Search overdue customers..."
                  className="h-12 w-full rounded-xl border border-gray-300 pl-11 pr-4 text-sm outline-none transition focus:border-green-500 focus:ring-4 focus:ring-green-100"
                />
              </div>
            </div>
          )}

          {loading ? (
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="h-52 animate-pulse rounded-2xl border border-gray-200 bg-white" />
              ))}
            </div>
          ) : filteredLoans.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-16 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-green-50 text-green-600">
                <MdWarningAmber className="text-2xl" />
              </div>
              <p className="mt-4 text-lg font-semibold text-gray-700">
                {loans.length === 0 ? 'No overdue loans' : 'No matching overdue accounts'}
              </p>
              <p className="mt-2 text-sm text-gray-400">
                {loans.length === 0
                  ? 'Customers with missed repayments will appear here.'
                  : 'Try a different customer name or email.'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredLoans.map(loan => {
                const defaults = Array.isArray(loan.defaults) ? loan.defaults : []
                const expanded = expandedEmail === loan.email

                return (
                  <article
                    key={loan.email}
                    className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
                  >
                    <div className="p-5 sm:p-6">
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-red-100 bg-red-50 px-2.5 py-1 text-[11px] font-bold uppercase text-red-700">
                              <MdWarningAmber />
                              {loan.days_overdue || 0} days overdue
                            </span>
                          </div>

                          <h2 className="mt-3 text-lg font-bold text-gray-900">
                            {loan.fullname || 'Unnamed customer'}
                          </h2>
                          <p className="mt-1 text-sm text-gray-500">{loan.email}</p>
                        </div>

                        <div className="rounded-xl bg-red-50 px-4 py-3 lg:text-right">
                          <p className="text-xs font-semibold text-red-700">Outstanding balance</p>
                          <p className="mt-1 text-xl font-bold text-gray-900">
                            {formatCurrency(loan.outstanding_balance)}
                          </p>
                        </div>
                      </div>

                      <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
                        <Metric label="Monthly repayment" value={formatCurrency(loan.monthly_repayment)} />
                        <Metric label="Default charges" value={formatCurrency(loan.total_default_amount)} danger />
                        <Metric label="Installments left" value={`${loan.installments_left ?? '—'}/12`} />
                        <Metric label="Payment due" value={formatDate(loan.next_payment_date)} />
                      </div>

                      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
                        <p className="text-sm text-gray-500">
                          <span className="font-semibold text-gray-700">Unpaid default entries:</span>{' '}
                          {loan.unpaid_default_count ?? defaults.length}
                        </p>

                        <button
                          type="button"
                          onClick={() => setExpandedEmail(expanded ? null : loan.email)}
                          className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700 transition hover:text-gray-900"
                        >
                          {expanded ? 'Hide history' : 'View default history'}
                          {expanded ? <MdExpandLess /> : <MdExpandMore />}
                        </button>
                      </div>
                    </div>

                    {expanded && (
                      <div className="border-t border-gray-200 bg-gray-50/70 p-5 sm:p-6">
                        <h3 className="font-bold text-gray-900">Default history</h3>

                        {defaults.length === 0 ? (
                          <p className="mt-3 text-sm text-gray-400">No default entries found.</p>
                        ) : (
                          <div className="mt-4 overflow-hidden rounded-xl border border-gray-200 bg-white">
                            <div className="overflow-x-auto">
                              <table className="min-w-[650px] w-full text-left text-sm">
                                <thead className="bg-gray-50 text-[11px] font-bold uppercase tracking-wider text-gray-500">
                                  <tr>
                                    <th className="px-4 py-3">Date</th>
                                    <th className="px-4 py-3">Charge</th>
                                    <th className="px-4 py-3">Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {defaults.map((item, index) => (
                                    <tr key={`${item.date || 'default'}-${index}`} className="border-t border-gray-100">
                                      <td className="px-4 py-3 text-gray-600">{formatDate(item.date)}</td>
                                      <td className="px-4 py-3 font-semibold text-gray-900">{formatCurrency(item.amount)}</td>
                                      <td className="px-4 py-3">
                                        <span className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase ${
                                          item.status === 'paid'
                                            ? 'border-green-100 bg-green-50 text-green-700'
                                            : 'border-red-100 bg-red-50 text-red-700'
                                        }`}>
                                          {item.status || 'unpaid'}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </article>
                )
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  )
}

function Metric({ label, value, danger = false }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{label}</p>
      <p className={`mt-1 text-base font-bold ${danger ? 'text-red-600' : 'text-gray-900'}`}>
        {value}
      </p>
    </div>
  )
}
